import React, { useEffect, useRef } from 'react';
import { Row, Column, Label, Toggle, Input, Text, Icon, Block, Tooltip, Button } from '@upsales/components';
import bemClass from '@upsales/components/Utils/bemClass';
import translate from 'Components/Helpers/translate';
import StepIntInput from 'Components/Inputs/StepIntInput';
import MailContentEditorSelectImage from './MailContentEditorSelectImage';
import MailContentEditorAlign from './MailContentEditorAlign';
import File from 'App/babel/resources/File';
import './MailContentEditorImage.scss';
import Elevio from 'App/babel/components/LegacyReactTemplates/elevio';
import ColorPicker from 'App/babel/components/ColorPicker/ColorPicker';
import openModal from 'App/services/Modal';
import { Feature, useFeatureAvailable } from 'App/components/hooks/featureHelper';

const MAX_BORDER = 20;
const MAX_RADIUS = 200;

const libraryFilename = (src, prefix) => {
	if (!src || !src.startsWith(prefix)) {
		return null;
	}
	try {
		return decodeURIComponent(src.slice(prefix.length));
	} catch (error) {
		// a malformed escape is not a filename the library can have
		return null;
	}
};

// noFrame: the PDF document templates reuse this panel, and their renderer draws no border or corners.
const MailContentEditorImage = ({ column, onChange, maxWidth, isVideo, accountProfile, noFrame }) => {
	const classes = new bemClass('MailContentEditor');
	const componentClasses = new bemClass('MailContentEditorImage');
	// Only an image in the account's own public folder can be fetched back by file id, and cropping
	// publishes the result, which the api gates on the documents licence.
	const hasDocuments = useFeatureAvailable(Feature.DOCUMENTS);
	const cropName = hasDocuments ? libraryFilename(column.src, File.getPublicUrl('')) : null;

	const openCrop = () =>
		openModal('ImageCropModal', {
			filename: cropName,
			onClose: cropped => {
				if (cropped) {
					onChange({
						...column,
						src: File.getPublicUrl(cropped.filename),
						realImgWidth: cropped.width,
						imgWidth: Math.min(column.imgWidth || cropped.width, cropped.width),
						userImgWidth: Math.min(column.userImgWidth || cropped.width, cropped.width)
					});
				}
			}
		});

	const getVimeoThumbnailURI = async url => {
		try {
			const res = await fetch(`//vimeo.com/api/oembed.json?url=${url}&width=${maxWidth}`);
			const json = await res.json();
			if (json.domain_status_code === 403) {
				// If the video has disabled embed on certain domains we cannot get the thumbnail with a play button, so we fetch the regular thumbnail
				const videoId = url.split('vimeo.com/')[1].split('?')[0];
				const resAlt = await fetch(`//vimeo.com/api/v2/video/${videoId}.json`);
				const jsonAlt = await resAlt.json();
				return jsonAlt[0].thumbnail_large;
			}
			return json.thumbnail_url_with_play_button;
		} catch (e) {
			console.log('Could not get Vimeo thumbnail:', e);
		}
		return '';
	};

	const getWistiaThumbnailURI = async url => {
		try {
			const res = await fetch(`//fast.wistia.com/oembed?url=${url}&width=${maxWidth}`);
			const json = await res.json();
			const { fileName } = await File.compose(json.thumbnail_url, 'addYouTubePlayButton');
			return File.getPublicUrl(fileName);
		} catch (e) {
			console.log('Could not get Wistia thumbnail:', e);
		}
		return '';
	};

	const extractYouTubeVideoId = url => {
		let videoId = '';

		const regex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|.*[?&]v=))([^&#?]+)/;
		const match = regex.exec(url);
		if (match && match[1]) {
			videoId = match[1];
		}

		return videoId;
	};

	const getYouTubeThumbnailURI = async url => {
		const videoId = extractYouTubeVideoId(url);

		if (!videoId) {
			return '';
		}

		try {
			const { fileName } = await File.compose(
				`https://img.youtube.com/vi/${videoId}/0.jpg`,
				'addYouTubePlayButton'
			);
			return File.getPublicUrl(fileName);
		} catch (e) {
			console.log('Could not get YouTube thumbnail:', e);
		}
		return '';
	};

	const getVideoThumbnail = async href => {
		let src = '';
		if (href.indexOf('youtu') !== -1) {
			src = await getYouTubeThumbnailURI(href);
		} else if (href.indexOf('wistia') !== -1) {
			src = await getWistiaThumbnailURI(href);
		} else if (href.indexOf('vimeo') !== -1) {
			src = await getVimeoThumbnailURI(href);
		}
		onChange({ ...column, href, src });
	};

	const t = useRef(null);
	useEffect(() => {
		clearTimeout(t.current);
		const link = column.href;
		t.current = setTimeout(() => {
			if (isVideo) {
				getVideoThumbnail(link);
			} else {
				onChange({ ...column, link });
			}
		}, 1000);
	}, [column.href]);

	const scallingMaxWidth = column.initialFixedWidth - column.hPadding * 2;

	return (
		<div className={componentClasses.b()}>
			{!isVideo ? (
				<Row className={classes.elem('sidebar-section-input-row').b()}>
					<Column>
						<Label>{translate('mail.imgPath')}</Label>
						<MailContentEditorSelectImage
							column={column}
							value={column.src}
							onChange={e =>
								onChange({ ...column, src: e.src, realImgWidth: e.realWidth, imgWidth: e.imageWidth })
							}
						/>
						{cropName ? (
							<Block space="mtm">
								<Button type="lined" size="sm" onClick={openCrop}>
									<Icon name="crop" space="mrs" />
									{translate('editor.cropImage')}
								</Button>
							</Block>
						) : null}
					</Column>
				</Row>
			) : null}
			<Row className={classes.elem('sidebar-section-input-row').b()}>
				<Column>
					<Label>{translate('editor.imageWidth')}</Label>
				</Column>
				<Column align="right">
					<StepIntInput
						value={column.imgWidth}
						onChange={e => onChange({ ...column, imgWidth: e.target.value, userImgWidth: e.target.value })}
						max={scallingMaxWidth}
						min={0}
						size="sm"
					/>
				</Column>
			</Row>
			<Row className={classes.elem('sidebar-section-input-row').b()}>
				<Column>
					<div className={componentClasses.elem('image-size').b()}>
						<Row>
							<Column>
								<Text size="sm" color="grey-12">
									{`${translate('mail.realImagesize')} `} <span> {`${column.realImgWidth}px`} </span>{' '}
								</Text>
							</Column>
							<Column align="right">
								<Block space="mrm">
									<Text size="sm" color="grey-10" italic>
										{`(${translate('mail.imageWidthRecomended')} ${scallingMaxWidth}px)`}{' '}
									</Text>
								</Block>
							</Column>
						</Row>
						<Row>
							<Column size={10}>
								<Elevio articleId={1122} sidebar>
									<Text
										size="sm"
										color="bright-blue"
										className="MailContentEditorImage__image-size--infoArticle"
									>
										<Icon name="question-circle" color="bright-blue" />{' '}
										{`${translate('mail.aboutRetinaImage')}`}
									</Text>
								</Elevio>
							</Column>
							{column.realImgWidth >= 2 * column.imgWidth ? (
								<Column align="right">
									<Block space="mrm">
										<Tooltip title={`${translate('mail.retinaMessage')}`} position="bottom">
											<Text size="sm" color="bright-green">
												<Icon name="check-circle" color="bright-green" />{' '}
												{`${translate('mail.retina')}`}
											</Text>
										</Tooltip>
									</Block>
								</Column>
							) : null}
						</Row>
					</div>
				</Column>
			</Row>
			{!column.expand ? (
				<Row className={classes.elem('sidebar-section-input-row').b()}>
					<Column>
						<MailContentEditorAlign
							value={column.imgAlign || 'center'}
							onChange={imgAlign => onChange({ ...column, imgAlign })}
						/>
					</Column>
				</Row>
			) : null}
			{noFrame ? null : (
				<>
					<Row className={classes.elem('sidebar-section-input-row').b()}>
						<Column>
							<Label>{translate('mail.border')}</Label>
						</Column>
						<Column align="right">
							<StepIntInput
								value={column.imgBorderWidth || 0}
								onChange={e => onChange({ ...column, imgBorderWidth: e.target.value })}
								max={MAX_BORDER}
								min={0}
								size="sm"
							/>
						</Column>
					</Row>
					{column.imgBorderWidth ? (
						<Row className={classes.elem('sidebar-section-input-row').b()}>
							<Column>
								<Label>{translate('mail.borderColor')}</Label>
							</Column>
							<Column align="right">
								<ColorPicker
									value={column.imgBorderColor}
									presets={accountProfile?.colorVariables}
									onChange={color => onChange({ ...column, imgBorderColor: color })}
								/>
							</Column>
						</Row>
					) : null}
					<Row className={classes.elem('sidebar-section-input-row').b()}>
						<Column>
							<Label>{translate('mail.borderRadius')}</Label>
						</Column>
						<Column align="right">
							<StepIntInput
								value={column.imgBorderRadius || 0}
								onChange={e => onChange({ ...column, imgBorderRadius: e.target.value })}
								max={MAX_RADIUS}
								min={0}
								size="sm"
							/>
						</Column>
					</Row>
				</>
			)}
			<Row className={classes.elem('sidebar-section-input-row').b()}>
				<Column>
					<Label>{translate(isVideo ? 'landingpage.videoAddress' : 'default.link')}</Label>
					{isVideo ? (
						<Text size="sm" color="grey-10">
							{translate('editor.supportedVideoUrls')}
						</Text>
					) : null}
					<Input
						value={column.href}
						onChange={e => onChange({ ...column, href: e.target.value })}
						placeholder={isVideo ? 'https://youtube.com/watch?v=xyz' : 'https://upsales.com'}
					/>
				</Column>
			</Row>
			<Row className={classes.elem('sidebar-section-input-row').b()}>
				<Column>
					<Label>{translate('mail.imageText')}</Label>
				</Column>
				<Column align="right" size={3}>
					<Toggle
						color="medium-green"
						checked={column.showCaption}
						onChange={val => onChange({ ...column, showCaption: val })}
					/>
				</Column>
			</Row>
			{column.showCaption ? (
				<div>
					<div className={classes.elem('sidebar-section-input-row').b()}>
						<MailContentEditorAlign
							value={column.captionAlign}
							onChange={captionAlign => onChange({ ...column, captionAlign })}
						/>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default MailContentEditorImage;
